import React, { useState, useMemo } from "react";
import {
  useStatesQuery,
  useDeleteStateMutation,
} from "../../services/basicData/basicData";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Container } from "reactstrap";
import { Trans } from "react-i18next";
import ConfirmModal from "../../CommonElements/ConfirmModal";
import { useQueryClient } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  DeleteIcon,
  Edit02Icon,
  AddSquareIcon,
  LocationIcon,
} from "@hugeicons/core-free-icons";
import { TableC } from "../../CommonElements/TableC";
import BtnAsLink from "../../Component/BtnAsLink";
import { useClientSearch } from "@/utils/hooks/useClientSearch";

// Empty state component for when no states are found
const EmptyState = ({ hasSearch }) => {
  const { t } = useTranslation();

  return (
    <div className="empty-state text-center py-5">
      <div className="empty-state-icon mb-3">
        <HugeiconsIcon icon={LocationIcon} size={48} className="text-muted" />
      </div>
      <h4 className="empty-state-title mb-2">{t("NO_STATES_FOUND")}</h4>
      <p className="empty-state-description text-muted mb-4">
        {t("NO_STATES_DESCRIPTION")}
      </p>
      {!hasSearch && (
        <Link to="create" className="btn btn-primary">
          <HugeiconsIcon icon={LocationIcon} size={16} className="me-2" />
          {t("CREATE_FIRST_STATE")}
        </Link>
      )}
    </div>
  );
};

export const States = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    stateId: null,
  });
  const { data, isLoading, isFetching } = useStatesQuery({
    page: currentPage,
    per_page: 10,
  });
  const states = data?.data?.data ?? [];
  const { filteredData, ...restClientSearch } = useClientSearch(states);
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const loading = isLoading || isFetching;

  // Delete mutation
  const { mutate: deleteState, isPending: isDeleting } =
    useDeleteStateMutation();

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle delete modal
  const handleDeleteClick = (stateId) => {
    setDeleteModalState({ isOpen: true, stateId });
  };

  const handleDeleteCancel = () => {
    setDeleteModalState({ isOpen: false, stateId: null });
  };

  const handleDeleteConfirm = () => {
    if (deleteModalState.stateId) {
      deleteState(deleteModalState.stateId, {
        onSuccess: () => {
          setDeleteModalState({ isOpen: false, stateId: null });
          queryClient.invalidateQueries({ queryKey: ["states"] });
        },
      });
    }
  };

  // Define columns configuration
  const columns = useMemo(
    () => [
      {
        name: t("NAME"),
        key: "name",
        filterable: true,
      },
      {
        name: t("CODE"),
        key: "code",
        filterable: true,
      },
      { name: t("ACTIONS"), key: "actions", filterable: false },
    ],
    [t]
  );

  // Define actions function
  const getActions = (rowData) => {
    return [
      {
        key: "edit",
        label: (
          <Link
            to={`/app/makkah/basic-data/states/${rowData.id}/edit`}
            className="d-flex align-items-center gap-2"
          >
            <HugeiconsIcon size={16} icon={Edit02Icon} />
            {t("EDIT")}
          </Link>
        ),
      },
      {
        type: "divider",
      },
      {
        key: "delete",
        label: (
          <div className="d-flex align-items-center gap-2">
            <HugeiconsIcon size={16} icon={DeleteIcon} />
            {t("DELETE")}
          </div>
        ),
        onClick: () => handleDeleteClick(rowData.id),
        danger: true,
      },
    ];
  };

  // Create button component
  const createButton = (
    <BtnAsLink
      className="btn-primary"
      icon={<HugeiconsIcon icon={AddSquareIcon} />}
      link="create"
      disabled={loading}
    >
      {t("CREATE_STATE")}
    </BtnAsLink>
  );

  return (
    <Container fluid>
      <TableC
        data={filteredData}
        {...restClientSearch}
        columns={columns}
        loading={loading}
        actions={getActions}
        renderActions={createButton}
        emptyState={EmptyState}
        minVisibleColumns={2}
        skeletonCount={3}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalState.isOpen}
        onToggle={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("CONFIRM")}
        message={
          <Trans
            i18nKey="CONFIRM_DELETE_STATE"
            components={{
              danger: <span className="text-danger" />,
            }}
          />
        }
        dangerText={t("DELETE")}
        confirmText={t("DELETE")}
        cancelText={t("CANCEL")}
        isLoading={isDeleting}
        defaultLoadingText={t("DELETING")}
        loadingText={t("DELETING")}
        confirmVariant="danger"
        confirmIcon={DeleteIcon}
      />
    </Container>
  );
};
